import React from 'react';
import HomeNav from '../navigations/homenav';
import Footer from '../navigations/Footer';
import {
  UserGroupIcon,
  ShieldCheckIcon,
  ScaleIcon,
  ClipboardDocumentListIcon,
  BuildingOfficeIcon,
  UsersIcon,
  AcademicCapIcon,
  EyeIcon,
  LightBulbIcon,
  HeartIcon,
  HandRaisedIcon,
  ChevronDownIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { useLanguageContext } from '../navigations/homenav';

// Leadership Profile Card Component
const LeadershipProfileCard = ({ name, title, imageUrl, bio, achievements, quote, isPresident = false }) => {
  const { t } = useLanguageContext();
  return (
  <div className={`bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 ${isPresident ? 'ring-2 ring-teal-500' : ''}`}>
    <div className="text-center mb-6">
      <div className={`w-32 h-32 mx-auto rounded-full overflow-hidden ${isPresident ? 'ring-4 ring-teal-200' : ''}`}>
        <img
          className="w-full h-full object-cover"
          src={imageUrl}
          alt={name}
        />
      </div>
      <h3 className="mt-6 text-2xl font-bold text-gray-800">{name}</h3>
      <p className="text-teal-600 font-medium text-lg">{title}</p>
    </div>

    <div className="space-y-6">
      <div>
        <h4 className="font-semibold text-gray-800 mb-3 text-lg">{t('leadershipAboutLabel')}</h4>
        <p className="text-gray-600 leading-relaxed">{bio}</p>
      </div>

      <div>
        <h4 className="font-semibold text-gray-800 mb-3 text-lg">{t('leadershipFocusLabel')}</h4>
        <ul className="space-y-2">
          {achievements.map((achievement, index) => (
            <li key={index} className="flex items-start text-gray-600">
              <span className="text-teal-500 mr-3 text-lg">✓</span>
              {achievement}
            </li>
          ))}
        </ul>
      </div>

      {quote && (
        <div className="bg-teal-50 p-4 rounded-lg border-l-4 border-teal-500">
          <p className="text-gray-700 italic">"{quote}"</p>
        </div>
      )}
    </div>
  </div>
  );
};

// Governance Role Card Component
const GovernanceRoleCard = ({ icon, title, description, responsibilities, members }) => {
  const { t } = useLanguageContext();
  const membersText = members || t('leadershipMembersVaries');
  return (
  <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer">
    <div className="flex items-center mb-4">
      <div className="flex items-center justify-center h-14 w-14 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white mr-4 group-hover:scale-110 transition-transform duration-300">
        {icon}
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-800">{title}</h3>
        
      </div>
    </div>
    <p className="text-gray-600 mb-4 leading-relaxed">{description}</p>
    <div>
      <h4 className="font-semibold text-gray-800 mb-2">{t('leadershipKeyResponsibilities')}</h4>
      <ul className="text-gray-600 text-sm space-y-1">
        {responsibilities.map((resp, index) => (
          <li key={index} className="flex items-start">
            <span className="text-teal-500 mr-2">•</span>
            {resp}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

};
// Interactive Organizational Chart Component
const OrganizationalChart = () => {
  const { t } = useLanguageContext();
  return (
  <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg">
    <h3 className="text-2xl font-bold text-gray-800 mb-8 text-center">{t('leadershipOrgStructureTitle')}</h3>

    {/* Top Level - President */}
    <div className="flex justify-center">
      <div className="bg-gradient-to-br from-teal-600 to-cyan-600 text-white px-8 py-6 rounded-full shadow-md text-center">
        <UserGroupIcon className="h-8 w-8 mx-auto mb-2" />
        <div className="text-sm font-semibold">{t('leadershipPresident')}</div>
        
      </div>
    </div>
    <div className="flex flex-col items-center my-4">
      <div className="w-0.5 h-6 bg-teal-300"></div>
      <ChevronDownIcon className="h-4 w-4 text-teal-400" />
    </div>

    {/* Second Level - Board of Directors | Executive Committee */}
    <div className="relative max-w-4xl mx-auto mb-8">
      <div className="w-full h-0.5 bg-teal-300 mb-6"></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Board of Directors */}
        <div className="bg-white px-5 py-4 rounded-xl shadow-sm ring-1 ring-gray-200 text-center">
          <ShieldCheckIcon className="h-6 w-6 mx-auto mb-2 text-cyan-600" />
          <div className="text-sm font-semibold text-gray-800">{t('leadershipBoardOfDirectors')}</div>
          <div className="text-xs text-gray-600">{t('leadershipStrategicOversight')}</div>
        </div>
        {/* Executive Committee */}
        <div className="bg-white px-5 py-4 rounded-xl shadow-sm ring-1 ring-gray-200 text-center">
          <UsersIcon className="h-6 w-6 mx-auto mb-2 text-teal-600" />
          <div className="text-sm font-semibold text-gray-800">{t('leadershipExecutiveCommittee')}</div>
          <div className="text-xs text-gray-600">{t('leadershipDailyManagement')}</div>
        </div>
        {/* Connector under Board of Directors (placed in next row, first column) */}
        <div className="hidden md:flex flex-col items-center md:col-span-1">
          <div className="w-0.5 h-6 bg-teal-300"></div>
          <ChevronDownIcon className="h-4 w-4 text-teal-400" />
        </div>
      </div>
    </div>

    {/* Third Level - Under Board of Directors: 3 nodes (aligned beneath Board) */}
    <div className="relative max-w-6xl mx-auto">
      <div className="grid grid-cols-1 gap-8">
        {/* Column under Board of Directors */}
        <div>
          {/* vertical connector from Board */}
          <div className="hidden"></div>
          {/* horizontal connector to three children */}
          <div className="hidden md:block mt-1 mb-5">
            <div className="w-full h-0.5 bg-teal-300"></div>
          </div>
          {/* children of Board of Directors */}
          <div className="md:flex md:flex-row md:flex-nowrap md:items-start md:justify-between gap-6 md:gap-8 md:pl-0">
            {/* Member Affairs Coordination */}
            <div className="text-center flex-none">
              <div className="bg-white p-5 rounded-xl shadow-sm ring-1 ring-gray-200">
                <HeartIcon className="h-6 w-6 mx-auto mb-2 text-teal-600" />
                <div className="text-sm font-semibold text-gray-800">{t('leadershipMemberAffairs')}</div>
                <div className="text-xs text-gray-600">{t('leadershipMembersOPDsGrassroots')}</div>
              </div>
            </div>
            {/* General Manager */}
            <div className="text-center flex-none md:w-[480px]">
              <div className="bg-white p-5 rounded-xl shadow-sm ring-1 ring-gray-200">
                <UsersIcon className="h-6 w-6 mx-auto mb-2 text-cyan-600" />
                <div className="text-sm font-semibold text-gray-800">{t('leadershipGeneralManager')}</div>
                <div className="text-xs text-gray-600">{t('leadershipDayToDayCoord')}</div>
              </div>
              {/* connectors under GM */}
              <div className="flex flex-col items-center mt-3">
                <div className="w-0.5 h-6 bg-teal-300"></div>
                <ChevronDownIcon className="h-4 w-4 text-teal-400" />
              </div>
              {/* GM Children */}
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-teal-50 rounded-lg px-4 py-3 ring-1 ring-teal-100">
                  <ClipboardDocumentListIcon className="h-5 w-5 mx-auto mb-1 text-teal-600" />
                  <div className="text-[12px] font-semibold text-gray-800">{t('leadershipVariousCommittees')}</div>
                </div>
                <div className="bg-teal-50 rounded-lg px-4 py-3 ring-1 ring-teal-100">
                  <ScaleIcon className="h-5 w-5 mx-auto mb-1 text-teal-600" />
                  <div className="text-[12px] font-semibold text-gray-800">{t('leadershipFinanceAdmin')}</div>
                </div>
                <div className="bg-teal-50 rounded-lg px-4 py-3 ring-1 ring-teal-100">
                  <LightBulbIcon className="h-5 w-5 mx-auto mb-1 text-teal-600" />
                  <div className="text-[12px] font-semibold text-gray-800">{t('leadershipCoordProjectPrintingIdentity')}</div>
                </div>
              </div>
            </div>
            {/* Office */}
            <div className="text-center flex-none">
              <div className="bg-white p-5 rounded-xl shadow-sm ring-1 ring-gray-200">
                <BuildingOfficeIcon className="h-6 w-6 mx-auto mb-2 text-teal-600" />
                <div className="text-sm font-semibold text-gray-800">{t('leadershipOffice')}</div>
                <div className="text-xs text-gray-600">{t('leadershipCentralBranches')}</div>
              </div>
              {/* connector under Office */}
              <div className="flex flex-col items-center mt-3">
                <div className="w-0.5 h-6 bg-teal-300"></div>
                <ChevronDownIcon className="h-4 w-4 text-teal-400" />
              </div>
              {/* Branch Office under Office */}
              <div className="mt-3 bg-teal-50 rounded-lg px-4 py-3 ring-1 ring-teal-100">
                <BuildingOfficeIcon className="h-5 w-5 mx-auto mb-1 text-teal-600" />
                <div className="text-[12px] font-semibold text-gray-800">{t('leadershipBranchOffice')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    
  </div>
  );
};

const LeadershipPage = () => {
  const { t } = useLanguageContext();

  return (
    <div className="bg-gray-50 text-gray-800">
      <HomeNav />

      <main>
        {/* Hero Section */}
        <section
          className="py-24 bg-center text-white relative"
          style={{
            backgroundImage: "url('/homeimagemaingovernance.jpeg')",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "top center",
            minHeight: "520px"
          }}
        >
          <div className="absolute inset-0 bg-slate-800 bg-opacity-20"></div>
          <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
            <UserGroupIcon className="h-16 w-16 mx-auto mb-6" />
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight">
              {t('leadershipHeroTitle')}
            </h1>
            <p className="mt-4 text-lg md:text-xl text-white max-w-3xl mx-auto">
              {t('leadershipHeroSubtitle')}
            </p>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {t('leadershipIntroTitle')}
            </h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              {t('leadershipIntroDesc')}
            </p>
          </div>
        </section>

        {/* Dr. Woinshet Profile Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('leaderAboutWoinshetTitle')}
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-teal-500 to-cyan-500 mx-auto"></div>
            </div>

            <div className="max-w-6xl mx-auto">
              <LeadershipProfileCard
                name={t('leaderWoinshetName')}
                title={t('leaderWoinshetTitle')}
                imageUrl="/dr.woinshet3.jpeg"
                bio={t('leaderWoinshetBio')}
                achievements={[
                  t('leaderAchievement1'),
                  t('leaderAchievement2'),
                  t('leaderAchievement3'),
                  t('leaderAchievement4')
                ]}
                quote={t('leaderQuote')}
                isPresident={true}
              />
            </div>
          </div>
        </section>

        {/* Leadership Vision Section */}
        <section className="py-20 bg-teal-600 text-white">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('leadershipVisionTitle')}
            </h2>
            <div className="w-24 h-1 bg-white bg-opacity-50 mx-auto mb-8"></div>
            <blockquote className="text-xl md:text-2xl italic mb-6">
              "{t('leaderQuote')}"
            </blockquote>
            <p className="text-teal-100">— {t('leaderWoinshetName')} (Ph.D.)</p>
          </div>
        </section>

        {/* Organizational Chart Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('leadershipGovernanceTitle')}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {t('leadershipGovernanceDesc')}
              </p>
            </div>

            <div className="mb-16">
              <OrganizationalChart />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <GovernanceRoleCard
                icon={<UserGroupIcon className="h-8 w-8" />}
                title={t('leadershipRolePresidentTitle')}
                description={t('leadershipRolePresidentDesc')}
                members={t('leaderWoinshetName') + ', Ph.D.'}
                responsibilities={[
                  t('leadershipRolePresidentResp1'),
                  t('leadershipRolePresidentResp2'),
                  t('leadershipRolePresidentResp3'),
                  t('leadershipRolePresidentResp4')
                ]}
              />
              <GovernanceRoleCard
                icon={<ShieldCheckIcon className="h-8 w-8" />}
                title={t('leadershipBoardOfDirectors')}
                description={t('leadershipBoardDesc')}
                responsibilities={[
                  t('leadershipBoardResp1'),
                  t('leadershipBoardResp2'),
                  t('leadershipBoardResp3'),
                  t('leadershipBoardResp4')
                ]}
              />
              <GovernanceRoleCard
                icon={<UsersIcon className="h-8 w-8" />}
                title={t('leadershipExecutiveCommittee')}
                description={t('leadershipExecDesc')}
                responsibilities={[
                  t('leadershipExecResp1'),
                  t('leadershipExecResp2'),
                  t('leadershipExecResp3'),
                  t('leadershipExecResp4')
                ]}
              />
              <GovernanceRoleCard
                icon={<HeartIcon className="h-8 w-8" />}
                title={t('leadershipMemberAffairs')}
                description={t('leadershipMemberAffairsDesc')}
                responsibilities={[
                  t('leadershipMemberResp1'),
                  t('leadershipMemberResp2'),
                  t('leadershipMemberResp3'),
                  t('leadershipMemberResp4')
                ]}
              />
              <GovernanceRoleCard
                icon={<UsersIcon className="h-8 w-8" />}
                title={t('leadershipGeneralManager')}
                description={t('leadershipGeneralManagerDesc')}
                responsibilities={[
                  t('leadershipGMResp1'),
                  t('leadershipGMResp2'),
                  t('leadershipGMResp3'),
                  t('leadershipGMResp4')
                ]}
              />
              <GovernanceRoleCard
                icon={<BuildingOfficeIcon className="h-8 w-8" />}
                title={t('leadershipOffice')}
                description={t('leadershipOfficeDesc')}
                responsibilities={[
                  t('leadershipOfficeResp1'),
                  t('leadershipOfficeResp2'),
                  t('leadershipOfficeResp3'),
                  t('leadershipOfficeResp4')
                ]}
              />
              <GovernanceRoleCard
                icon={<BuildingOfficeIcon className="h-8 w-8" />}
                title={t('leadershipBranchOffice')}
                description={t('leadershipBranchOfficeDesc')}
                responsibilities={[
                  t('leadershipBranchResp1'),
                  t('leadershipBranchResp2'),
                  t('leadershipBranchResp3'),
                  t('leadershipBranchResp4')
                ]}
              />
              <GovernanceRoleCard
                icon={<ClipboardDocumentListIcon className="h-8 w-8" />}
                title={t('leadershipStandingTemporaryCommittees')}
                description={t('leadershipStandingCommitteesDesc')}
                responsibilities={[
                  t('leadershipCommitteesResp1'),
                  t('leadershipCommitteesResp2'),
                  t('leadershipCommitteesResp3'),
                  t('leadershipCommitteesResp4')
                ]}
              />
              <GovernanceRoleCard
                icon={<ScaleIcon className="h-8 w-8" />}
                title={t('leadershipFinanceAdmin')}
                description={t('leadershipFinanceAdminDesc')}
                responsibilities={[
                  t('leadershipFinanceResp1'),
                  t('leadershipFinanceResp2'),
                  t('leadershipFinanceResp3'),
                  t('leadershipFinanceResp4')
                ]}
              />
              <GovernanceRoleCard
                icon={<LightBulbIcon className="h-8 w-8" />}
                title={t('leadershipCoordProjectPrintingIdentity')}
                description={t('leadershipCoordPPIRespDesc')}
                responsibilities={[
                  t('leadershipPPIResp1'),
                  t('leadershipPPIResp2'),
                  t('leadershipPPIResp3'),
                  t('leadershipPPIResp4')
                ]}
              />
            </div>
          </div>
        </section>

        {/* Governance Tagline Section */}
        <section className="py-20 bg-gradient-to-r from-teal-500 via-cyan-500 to-teal-500 text-white">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">
              {t('leadershipPrinciplesTitle')}
            </h2>
            <div className="bg-white bg-opacity-10 p-8 rounded-2xl backdrop-blur-sm">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                {t('leadershipPrinciplesHeadline')}
              </h3>
              <p className="text-xl text-teal-100">
                {t('leadershipPrinciplesSubAm')}
              </p>
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {t('leadershipCtaTitle')}
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              {t('leadershipCtaDesc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contactus"
                className="inline-block px-8 py-3 rounded-lg text-lg font-medium bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:from-teal-600 hover:to-cyan-600 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {t('getInvolved')}
              </a>
              <a
                href="/about/mission-vision-values"
                className="inline-block px-8 py-3 rounded-lg text-lg font-medium border-2 border-teal-500 text-teal-600 hover:bg-teal-500 hover:text-white transition-all duration-300"
              >
                {t('ourMissionButton')}
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default LeadershipPage;
